import React from 'react';
import type { Participant, VoteSummary } from '../../types/index';
import { Button } from '../common/Button';

interface PlayerPanelProps {
  participants?: Participant[];
  state?: 'collecting' | 'revealed';
  voteSummary?: VoteSummary | null;
  isFacilitator?: boolean;
  onReset?: () => void;
}

export function PlayerPanel({ 
  participants = [], 
  state = 'collecting', 
  voteSummary,
  isFacilitator = false,
  onReset 
}: PlayerPanelProps) {
  // Ensure participants is always an array
  const safeParticipants = Array.isArray(participants) ? participants : [];
  const safeState = state || 'collecting';

  if (safeParticipants.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p className="text-lg mb-2">No participants yet</p>
        <p className="text-sm">Invite teammates to join!</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
            Participants ({safeParticipants.length})
          </h3>
        </div>
        
        {safeState === 'revealed' && voteSummary?.hasConsensus && voteSummary.consensusValue !== null && (
          <div className="mb-3 p-3 bg-green-50 border-2 border-green-400 rounded-lg">
            <div className="flex items-center gap-2">
              <svg
                className="w-5 h-5 text-green-600 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="min-w-0">
                <div className="font-bold text-green-800 text-sm">
                  Consensus Achieved!
                </div>
                <div className="text-green-700 text-xs">
                  All voted{' '}
                  <span className="font-semibold">
                    {voteSummary.consensusValue === '?' ? '?' : voteSummary.consensusValue}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className="space-y-2">
          {safeParticipants.map((participant) => (
            <div
              key={participant.id}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-3 h-3 rounded-full ${
                    participant.voted ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                  aria-label={participant.voted ? 'Voted' : 'Not voted'}
                />
                <span className="font-medium text-gray-800">{participant.name}</span>
              </div>
              {safeState === 'revealed' && participant.value !== undefined && (
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full font-semibold text-sm">
                  {participant.value === '?' ? '?' : participant.value}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      {safeState === 'revealed' && voteSummary && (
        <div className="border-t border-gray-200 pt-3">
          <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
            Statistics
          </h4>
          <div className="grid grid-cols-2 gap-3">
            {voteSummary.avg !== null && (
              <div>
                <div className="text-xs text-gray-500 uppercase">Avg</div>
                <div className="text-lg font-bold text-gray-800">
                  {voteSummary.avg.toFixed(1)}
                </div>
              </div>
            )}
            {voteSummary.mode !== null && (
              <div>
                <div className="text-xs text-gray-500 uppercase">Mode</div>
                <div className="text-lg font-bold text-gray-800">
                  {voteSummary.mode === '?' ? '?' : voteSummary.mode}
                </div>
              </div>
            )}
            {voteSummary.min !== null && (
              <div>
                <div className="text-xs text-gray-500 uppercase">Min</div>
                <div className="text-lg font-bold text-gray-800">{voteSummary.min}</div>
              </div>
            )}
            {voteSummary.max !== null && (
              <div>
                <div className="text-xs text-gray-500 uppercase">Max</div>
                <div className="text-lg font-bold text-gray-800">{voteSummary.max}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {isFacilitator && onReset && safeState === 'revealed' && (
        <div className="pt-3 border-t border-gray-200">
          <Button variant="secondary" size="sm" onClick={onReset} className="w-full">
            Reset Votes
          </Button>
        </div>
      )}
    </div>
  );
}

